import clsx from "clsx";
import { FunctionComponent, KeyboardEvent, useRef } from "react";

import {
  AnalyticsClient,
  useAnalytics,
} from "@voxmedia/duet-context/context/analytics-context";
import { useTheme } from "@voxmedia/duet-framework/components/app/ThemeProvider";

import * as styles from "./DrawerThemeSwitcher.css";

const SunIcon: FunctionComponent = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="5" />
    <path
      d="M12 1v3M12 20v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M1 12h3M20 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

/** Half-filled circle — system / auto preference. */
const SystemIcon: FunctionComponent = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path d="M12 3a9 9 0 0 1 0 18V3z" />
  </svg>
);

const MoonIcon: FunctionComponent = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const switchThemeFactory = (
  variant: string,
  cb: () => void,
  analytics?: AnalyticsClient | null,
) => {
  return () => {
    analytics?.emit({
      event: "theme_toggle",
      theme_toggle: {
        variant,
      },
    });
    cb();
  };
};

/**
 * CSK drawer theme switcher. Like VergeThemeSwitcher but passes `force: true`
 * so toggles override editorial `pageTheme` on article shells with a drawer switcher.
 */
const DrawerThemeSwitcher: FunctionComponent = () => {
  const themeCtx = useTheme();
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const analytics = useAnalytics();

  if (!themeCtx) {
    return null;
  }

  const { userSelectedTheme, setTheme, clearThemeOverride } = themeCtx;
  const isLight = userSelectedTheme === "vergeLight";
  const isDark = userSelectedTheme === "vergeDark";
  const isAuto = userSelectedTheme === null;

  const options = [
    {
      label: "Light",
      icon: <SunIcon />,
      isActive: isLight,
      activeClassName: styles.segmentActiveLight,
      onSelect: switchThemeFactory(
        "vergeLight",
        () => setTheme("vergeLight", true),
        analytics,
      ),
    },
    {
      label: "System",
      icon: <SystemIcon />,
      isActive: isAuto,
      activeClassName: styles.segmentActiveSystem,
      onSelect: switchThemeFactory("auto", clearThemeOverride, analytics),
    },
    {
      label: "Dark",
      icon: <MoonIcon />,
      isActive: isDark,
      activeClassName: styles.segmentActiveDark,
      onSelect: switchThemeFactory(
        "vergeDark",
        () => setTheme("vergeDark", true),
        analytics,
      ),
    },
  ] as const;

  const handleKeyDown =
    (index: number) => (e: KeyboardEvent<HTMLButtonElement>) => {
      let nextIndex: number | null = null;

      if (e.key === "ArrowRight") {
        nextIndex = (index + 1) % options.length;
      } else if (e.key === "ArrowLeft") {
        nextIndex = (index - 1 + options.length) % options.length;
      } else if (e.key === "Home") {
        nextIndex = 0;
      } else if (e.key === "End") {
        nextIndex = options.length - 1;
      }

      if (nextIndex !== null) {
        e.preventDefault();
        options[nextIndex]?.onSelect();
        buttonRefs.current[nextIndex]?.focus();
      }
    };

  return (
    <div className={styles.section}>
      <div className={styles.track} role="group" aria-label="Color theme">
        {options.map((option, index) => (
          <button
            key={option.label}
            type="button"
            className={clsx(
              styles.segment,
              option.isActive && option.activeClassName,
              index === 0 && styles.segmentFirst,
              index === options.length - 1 && styles.segmentLast,
            )}
            onClick={option.onSelect}
            onKeyDown={handleKeyDown(index)}
            aria-pressed={option.isActive}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
          >
            {option.icon}
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default DrawerThemeSwitcher;
